import { Badge, Tooltip, TooltipContent, TooltipTrigger } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useIsTruncated } from '@/shared/hooks/useIsTruncated/useIsTruncated';

import { ToolBadge } from '@/shared/ui/ToolBadge';
import type { ToolsTableV2RowData } from './ToolsTableV2.types';

interface ToolNameCellProps {
  tool: ToolsTableV2RowData;
  /** Where the row leads. A `<tr>` takes no focus, so this link is the keyboard's way in. */
  href?: string;
  /** Set only while the tool still needs configuring, which raises an error badge by its name. */
  unconfiguredCount?: number;
}

/**
 * Name over description, the way the Knowledge Hub's media guidelines table
 * stacks a record's title and its secondary line. The description has no column
 * of its own: it reads as a second line under the name, and a line too long for
 * the column spills into a tooltip instead of widening the row.
 *
 * The overflowing line takes focus so that tooltip opens from the keyboard as
 * well as the pointer — a hover-only tooltip would put the description out of
 * reach of anyone not using a mouse. A line that already fits renders as a
 * plain span, so it adds neither a tab stop nor a tooltip repeating what is
 * on screen.
 */
export function ToolNameCell({ tool, href, unconfiguredCount }: ToolNameCellProps) {
  const { t } = useTranslation('admin');
  const { setNode, isTruncated } = useIsTruncated();

  const description = (
    <span
      ref={setNode}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a tooltip trigger has to take focus, and this one is text rather than a control
      tabIndex={isTruncated ? 0 : undefined}
      className="block truncate rounded-sm text-body-sm text-muted-foreground outline-none focus-visible:focus-ring"
    >
      {tool.displayDescription}
    </span>
  );

  return (
    <div className="flex items-center gap-xs">
      <ToolBadge toolId={tool.id} category={tool.tag} size="sm" />
      <div className="flex min-w-0 max-w-100 flex-col gap-3xs">
        <span className="flex min-w-0 items-center gap-xs">
          {href ? (
            <Link
              to={href}
              data-testid="tool-name"
              className="truncate rounded-sm text-body-md font-medium text-foreground outline-none focus-visible:focus-ring"
              // The row navigates too; without this the same route is pushed twice.
              onClick={(event) => event.stopPropagation()}
            >
              {tool.displayName}
            </Link>
          ) : (
            <span
              data-testid="tool-name"
              className="truncate text-body-md font-medium text-foreground"
            >
              {tool.displayName}
            </span>
          )}
          {tool.builtAgent?.status && (
            <Badge
              variant={tool.builtAgent.status.tone === 'warning' ? 'warning' : 'secondary'}
              size="sm"
              roundness="full"
              className="shrink-0"
            >
              {tool.builtAgent.status.label}
            </Badge>
          )}
          {unconfiguredCount !== undefined && (
            <Badge variant="destructive" size="sm" roundness="full" className="shrink-0">
              {t('admin.toolsTable.toolsUnconfigured', { count: unconfiguredCount })}
            </Badge>
          )}
        </span>
        {tool.displayDescription !== '' &&
          (isTruncated ? (
            <Tooltip>
              <TooltipTrigger asChild>{description}</TooltipTrigger>
              {/* A full sentence, so the panel is capped rather than left to run
                  the width of the viewport. */}
              <TooltipContent className="max-w-sm">{tool.displayDescription}</TooltipContent>
            </Tooltip>
          ) : (
            description
          ))}
      </div>
    </div>
  );
}

ToolNameCell.displayName = 'ToolNameCell';
